import { I18n } from '@calendly/i18n'
import { Badge } from '@calendly/ui/components/badge'
import { Button } from '@calendly/ui/components/button'
import { IconCircleCheckFilled } from '@calendly/ui/components/glyphs/IconCircleCheckFilled'
import { IconExternalLink } from '@calendly/ui/components/glyphs/IconExternalLink'
import { Link } from '@calendly/ui/components/link'
import { Body, Footer, Header, Popup } from '@calendly/ui/components/popup'
import { BodyText } from '@calendly/ui/components/typography'
import { UiIcon } from '@calendly/ui/components/ui-icon'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { colorIconSecondary } from '@calendly/ui/theme'
import classNames from 'classnames'

import {
  bodyStackCss,
  featureIconCss,
  featureItemCss,
  featureListCss,
  footerPhoneCss,
  heroPanelCss,
  introContentCss,
  introContentPhoneCss,
  introHeaderPhoneCss,
  introShellCss,
  introShellPhoneCss,
  learnMoreLinkCss,
} from './brandRefreshIntroModalStyles'
import { BRAND_REFRESH_LEARN_MORE_HELP_URL } from './constants'

interface BrandRefreshIntroModalProps {
  daysRemaining: number
  onClose: () => void
  onTryItNow: () => void
}

export const BrandRefreshIntroModal = ({
  daysRemaining,
  onClose,
  onTryItNow,
}: BrandRefreshIntroModalProps) => {
  const isPhone = useDeviceType('isPhone')

  const introFeatures = [
    {
      title: I18n.t('publisher.app_shell.brand_refresh_beta.intro.cleaner_title'),
      description: I18n.t('publisher.app_shell.brand_refresh_beta.intro.cleaner_description'),
    },
    {
      title: I18n.t('publisher.app_shell.brand_refresh_beta.intro.navigation_title'),
      description: I18n.t('publisher.app_shell.brand_refresh_beta.intro.navigation_description'),
    },
    {
      title: I18n.t('publisher.app_shell.brand_refresh_beta.intro.carryover_title'),
      description: I18n.t('publisher.app_shell.brand_refresh_beta.intro.carryover_description'),
    },
  ] as const

  return (
    <Popup
      isOpen
      onClose={onClose}
      size="max-content"
      withCloseButton
    >
      <div
        className={classNames(introShellCss, { [introShellPhoneCss]: isPhone })}
        data-testid="brand-refresh-intro-modal"
      >
        {!isPhone && <div className={heroPanelCss} aria-hidden />}
        <div className={classNames(introContentCss, { [introContentPhoneCss]: isPhone })}>
          <div className={classNames({ [introHeaderPhoneCss]: isPhone })}>
            <Header
              eyebrowLabel={(
                <Badge type="new">
                  {I18n.t('publisher.app_shell.brand_refresh_beta.banner.badge')}
                </Badge>
              )}
              title={I18n.t('publisher.app_shell.brand_refresh_beta.banner.body', { count: daysRemaining })}
            />
          </div>
          <Body>
            <div className={bodyStackCss}>
              <BodyText color="secondary">
                {I18n.t('publisher.app_shell.brand_refresh_beta.intro.description')}
              </BodyText>
              <ul className={featureListCss}>
                {introFeatures.map(({ title, description }) => (
                  <li key={title} className={featureItemCss}>
                    <span className={featureIconCss}>
                      <UiIcon
                        icon={IconCircleCheckFilled}
                        color={colorIconSecondary}
                        size="medium"
                        title=""
                      />
                    </span>
                    <BodyText>
                      <BodyText inline weight="bold">
                        {title}
                      </BodyText>
                      {' '}
                      {description}
                    </BodyText>
                  </li>
                ))}
              </ul>
              {BRAND_REFRESH_LEARN_MORE_HELP_URL && (
                <Link
                  className={learnMoreLinkCss}
                  standalone
                  href={BRAND_REFRESH_LEARN_MORE_HELP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {I18n.t('publisher.app_shell.brand_refresh_beta.intro.learn_more_changes')}
                  <UiIcon icon={IconExternalLink} size="small" title="" />
                </Link>
              )}
            </div>
          </Body>
          <Footer>
            {isPhone ? (
              <div className={footerPhoneCss}>
                <Button
                  wide
                  onClick={onTryItNow}
                  data-testid="brand-refresh-intro-try-it-now"
                >
                  {I18n.t('publisher.app_shell.brand_refresh_beta.intro.update_now')}
                </Button>
              </div>
            ) : (
              <Button
                wide
                onClick={onTryItNow}
                data-testid="brand-refresh-intro-try-it-now"
              >
                {I18n.t('publisher.app_shell.brand_refresh_beta.intro.update_now')}
              </Button>
            )}
          </Footer>
        </div>
      </div>
    </Popup>
  )
}
